import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "@/lib/api";
import { Button, ErrorNote, errorMessage } from "@/components/ui";
import { Login } from "@/pages/Login";
import { JobsList } from "@/pages/JobsList";
import { JobForm } from "@/pages/JobForm";
import { Applications } from "@/pages/Applications";
import { ChangePassword } from "@/pages/ChangePassword";
import type { Recruiter, Sector } from "@/lib/types";

/**
 * Enrutado por hash, a mano. El portal tiene cinco pantallas y navegación
 * lineal; montar Tanstack Router (como notes-app) añadiría un árbol de
 * rutas y una dependencia para muy poco. El hash basta para que el
 * reclutador pueda marcar como favorita la bandeja de una oferta.
 *
 *   #/                    listado de ofertas
 *   #/new                 alta
 *   #/job/<id>            edición
 *   #/job/<id>/apps       candidaturas
 *   #/password            cambio de contraseña
 */
type View =
  | { name: "list" }
  | { name: "new" }
  | { name: "edit"; id: number }
  | { name: "apps"; id: number }
  | { name: "password" };

function parseHash(): View {
  const h = window.location.hash.replace(/^#\/?/, "");
  if (h === "new") return { name: "new" };
  if (h === "password") return { name: "password" };
  const m = h.match(/^job\/(\d+)(\/apps)?$/);
  if (m) {
    const id = parseInt(m[1], 10);
    return m[2] ? { name: "apps", id } : { name: "edit", id };
  }
  return { name: "list" };
}

function go(path: string) {
  window.location.hash = path;
}

export function App() {
  const [me, setMe] = useState<Recruiter | null>(null);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [view, setView] = useState<View>(parseHash);
  const [booted, setBooted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onHash = () => setView(parseHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Arranque: ¿hay cookie de sesión viva? El 401 es la respuesta normal
  // para un visitante anónimo, no un error que mostrar.
  useEffect(() => {
    (async () => {
      try {
        setMe(await api<Recruiter>("/api/recruiter/me"));
      } catch {
        setMe(null);
      } finally {
        setBooted(true);
      }
    })();
  }, []);

  useEffect(() => {
    if (!me) return;
    api<{ sectors: Sector[] }>("/api/jobs/sectors")
      .then((r) => setSectors(r.sectors))
      .catch(() => setSectors([]));
  }, [me]);

  const logout = useCallback(async () => {
    try {
      await api("/api/recruiter/auth/logout", { method: "POST" });
    } catch (e) {
      // Da igual que el POST falle: la sesión local se descarta igual y
      // la cookie caduca sola. No merece bloquear la salida.
      if (e instanceof ApiError && e.status !== 401) setError(errorMessage(e.code));
    }
    setMe(null);
    go("/");
  }, []);

  if (!booted) {
    return <p className="p-6 font-mono text-sm text-mute">Cargando…</p>;
  }

  if (!me) return <Login onLogin={setMe} />;

  // Cambio de contraseña forzado: la API bloquea el resto del portal con
  // 403 mientras el flag siga puesto, así que la UI ni siquiera lo ofrece.
  if (me.mustChangePassword) {
    return (
      <Shell me={me} onLogout={logout}>
        <ChangePassword forced onDone={() => setMe({ ...me, mustChangePassword: false })} />
      </Shell>
    );
  }

  return (
    <Shell me={me} onLogout={logout}>
      <ErrorNote>{error}</ErrorNote>
      {view.name === "list" && (
        <JobsList
          onNew={() => go("/new")}
          onEdit={(id) => go(`/job/${id}`)}
          onApplications={(id) => go(`/job/${id}/apps`)}
        />
      )}
      {view.name === "new" && (
        <JobForm
          jobId={null}
          sectors={sectors}
          defaultCompany={me.company}
          onDone={() => go("/")}
          onCancel={() => go("/")}
        />
      )}
      {view.name === "edit" && (
        <JobForm
          jobId={view.id}
          sectors={sectors}
          defaultCompany={me.company}
          onDone={() => go("/")}
          onCancel={() => go("/")}
        />
      )}
      {view.name === "apps" && <Applications jobId={view.id} onBack={() => go("/")} />}
      {view.name === "password" && (
        <ChangePassword forced={false} onDone={() => go("/")} onCancel={() => go("/")} />
      )}
    </Shell>
  );
}

function Shell({
  me,
  onLogout,
  children,
}: {
  me: Recruiter;
  onLogout: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="min-h-screen">
      <header className="border-b border-line">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3">
          <a href="#/" className="font-mono text-lg text-ink hover:text-accent">
            empleo<span className="text-accent">//</span>
          </a>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-xs text-mute">
              {me.username} · {me.company}
            </span>
            <a
              href="#/password"
              className="font-mono text-xs text-mute underline-offset-2 hover:text-accent hover:underline"
            >
              contraseña
            </a>
            <Button variant="ghost" onClick={onLogout}>
              salir
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6">{children}</main>
    </div>
  );
}
